
/**
 * Control Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class ControlService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /** ControlApi custom methods return raw axios responses; BaseApi methods return response.data already. */
    _axiosData(result) {
        if (result && typeof result === 'object' && 'data' in result && 'status' in result && 'config' in result) {
            return result.data;
        }
        return result;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
                pagination: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues,
                message: response.message || 'Control listesi getirildi',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch control list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch control');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Control oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create control');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Control güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update control');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Control silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete control');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to perform bulk delete');
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.batchDelete(data.ids || []);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    async getCurrentEvaluation(id) {
        try {
            const raw = await withRetry(
                () => this.api.getCurrentEvaluation(id),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Current control evaluation fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch current evaluation');
        }
    }

    async getStandardsMapping(id, params = {}) {
        try {
            const raw = await withRetry(
                () => this.api.getStandardsMapping(id, params),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? [],
                meta: body?.pagination || body?.meta || {},
                summary: body?.summary || {},
                message: body?.message || 'Control standards mapping fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch control standards mapping');
        }
    }

    async getScopeOptions() {
        try {
            const raw = await withRetry(
                () => this.api.getScopeOptions(),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? [],
                message: body?.message || 'Control scope options fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch control scope options');
        }
    }

    async getCategoryOptions() {
        try {
            const raw = await withRetry(
                () => this.api.getCategoryOptions(),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? [],
                message: body?.message || 'Control category options fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch control category options');
        }
    }

    async getScopePreference() {
        try {
            const raw = await withRetry(
                () => this.api.getScopePreference(),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? {
                    scope_standards: [],
                    category_domains: [],
                    catalog_seed_tags_filter: []
                },
                message: body?.message || 'Control scope preference fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch control scope preference');
        }
    }

    async updateScopePreference(payload = {}) {
        try {
            const raw = await this.api.updateScopePreference(payload);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? {
                    scope_standards: [],
                    category_domains: [],
                    catalog_seed_tags_filter: []
                },
                message: body?.message || 'Control scope preference updated'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update control scope preference');
        }
    }

    async getEvaluationHistory(id, params = {}) {
        try {
            const raw = await withRetry(
                () => this.api.getEvaluationHistory(id, params),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                meta: body?.pagination || body?.meta || {},
                uniqueValues: body?.uniqueValues || {},
                message: body?.message || 'Control evaluation history fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch evaluation history');
        }
    }

    async createEvaluationVersion(id, payload = {}) {
        try {
            const raw = await this.api.createEvaluationVersion(id, payload);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Control evaluation version created'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create evaluation version');
        }
    }

    async uploadEvaluationEvidence(id, payload, optionsOrCallback) {
        try {
            const raw = await this.api.uploadEvaluationEvidence(id, payload, optionsOrCallback);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Control evaluation evidence uploaded'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to upload evaluation evidence');
        }
    }

    async downloadEvaluationEvidence(id, storedName) {
        try {
            const raw = await this.api.downloadEvaluationEvidence(id, storedName);
            return this._axiosData(raw);
        } catch (error) {
            throw this._handleError(error, 'Failed to download evaluation evidence');
        }
    }

    async deleteEvaluationEvidence(id, storedName) {
        try {
            const raw = await this.api.deleteEvaluationEvidence(id, storedName);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Control evaluation evidence deleted'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete evaluation evidence');
        }
    }

    async getRelatedAssessmentQuestions(id, params = {}) {
        try {
            const raw = await withRetry(
                () => this.api.getRelatedAssessmentQuestions(id, params),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? [],
                linkedQuestionIds: body?.linkedQuestionIds ?? [],
                meta: body?.pagination || {},
                pagination: body?.pagination,
                message: body?.message || 'Related assessment questions fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related assessment questions');
        }
    }

    async linkAssessmentQuestions(id, payload = {}) {
        try {
            const raw = await this.api.linkAssessmentQuestions(id, payload);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Assessment questions linked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link assessment questions');
        }
    }

    async unlinkAssessmentQuestion(id, questionId) {
        try {
            const raw = await this.api.unlinkAssessmentQuestion(id, questionId);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Assessment question unlinked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink assessment question');
        }
    }

    async bulkUnlinkAssessmentQuestions(id, payload = {}) {
        try {
            const raw = await this.api.bulkUnlinkAssessmentQuestions(id, payload);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Assessment questions unlinked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk unlink assessment questions');
        }
    }

    async deleteRelatedAssessmentQuestion(id, questionId) {
        try {
            const raw = await this.api.deleteRelatedAssessmentQuestion(id, questionId);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Assessment question deleted'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete assessment question');
        }
    }

    async bulkDeleteRelatedAssessmentQuestions(id, payload = {}) {
        try {
            const raw = await this.api.bulkDeleteRelatedAssessmentQuestions(id, payload);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Assessment questions deleted'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete assessment questions');
        }
    }

    async getSoaList(params = {}) {
        try {
            const raw = await withRetry(
                () => this.api.getSoaList(params),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? [],
                summary: body?.summary ?? {},
                meta: body?.pagination || {},
                uniqueValues: body?.uniqueValues,
                message: body?.message || 'SoA listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch SoA list');
        }
    }

    async getSoaStats() {
        try {
            const raw = await withRetry(
                () => this.api.getSoaStats(),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch SoA statistics');
        }
    }

    async getRelatedDocuments(id, params = {}) {
        try {
            const raw = await withRetry(
                () => this.api.getRelatedDocuments(id, params),
                { maxRetries: 2 }
            );
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? [],
                linkedDocumentIds: body?.linkedDocumentIds ?? [],
                meta: body?.pagination || body?.meta || {},
                pagination: body?.pagination,
                message: body?.message || 'Related documents fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related documents');
        }
    }

    async linkDocuments(id, payload = {}) {
        try {
            const raw = await this.api.linkDocuments(id, payload);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Documents linked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link documents');
        }
    }

    async unlinkDocument(id, documentId) {
        try {
            const raw = await this.api.unlinkDocument(id, documentId);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Document unlinked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink document');
        }
    }

    async bulkUnlinkDocuments(id, payload = {}) {
        try {
            const raw = await this.api.bulkUnlinkDocuments(id, payload);
            const body = this._axiosData(raw);
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message || 'Documents unlinked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk unlink documents');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
